/**
 * Animation Handler - Orchestrates animation playback and transitions.
 *
 * This module handles:
 * - Animation frame rendering
 * - Transition playback from idle to animation and back
 * - Frame timing at 25fps
 * - Session state tracking
 *
 * The handler relies on server-sent packet flags (Transition, TransitionEnd, Idle)
 * to determine when to generate transitions, rather than maintaining complex internal state.
 *
 * @internal
 * @packageDocumentation
 */

import {
  logger,
  trackEvent,
  trackMetric,
  generateConversationId,
  recordMetric,
  startPlaybackTrace,
  peekTraceparent,
  resolveMonoTimestamp,
} from '../utils';

/**
 * Opaque animation frame data.
 * The actual shape is defined in web-sdk (KeyframeData).
 * rtc-sdk treats these as opaque — it only stores and passes them to the renderer.
 * @internal
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnimationFrame = any;

/**
 * Interface that applications must implement to render avatar frames.
 * The SDK calls these methods to control the avatar.
 * @internal
 */
export interface AvatarRenderer {
  /**
   * Render a single animation frame from raw protobuf data.
   * The renderer decodes the protobuf internally.
   * @param protobufData - Raw protobuf bytes containing animation keyframes
   */
  renderFromProtobuf(protobufData: ArrayBuffer): void;

  /**
   * Render a pre-decoded animation frame (e.g., transition frame).
   * @param frame - Opaque frame data, or undefined to render idle
   * @param startIdle - If true and frame is undefined, start idle animation loop
   */
  renderFrame(frame: AnimationFrame | undefined, startIdle?: boolean): void;

  /**
   * Generate transition frames from the most recently rendered frame to the
   * target decoded from protobuf. Used for idle → speaking.
   */
  generateTransitionToFrame(
    protobufData: ArrayBuffer,
    frameCount: number,
  ): Promise<AnimationFrame[]>;

  /**
   * Generate transition frames from the most recently rendered frame back to
   * the idle start. Used for speaking → idle and disconnect soft transitions.
   */
  generateTransitionToIdle(
    frameCount: number,
  ): Promise<AnimationFrame[]>;

  /**
   * Check if the renderer is available and ready.
   * @returns true if renderer is ready to accept frames
   */
  isReady(): boolean;
}

/**
 * Configuration for AnimationHandler.
 * @internal
 */
export interface AnimationHandlerConfig {
  /**
   * Number of transition frames when starting animation.
   * Default: 8 (~320ms at 25fps)
   */
  transitionStartFrameCount?: number;

  /**
   * Number of transition frames when ending animation.
   * Default: 20 (~800ms at 25fps)
   */
  transitionEndFrameCount?: number;

  /**
   * Callback when data stream stalls and fallback to idle is triggered.
   * Called when no animation frames received for 5 seconds during speaking session.
   */
  onStreamStalled?: () => void;

  /**
   * Enable jitter buffer for smoother playback.
   * When enabled, frames are buffered and rendered in sequence order at a steady 25fps,
   * absorbing network jitter and out-of-order delivery.
   * Default: true
   */
  enableJitterBuffer?: boolean;

  /**
   * Maximum delay (in ms) a frame can sit in the jitter buffer before being rendered.
   * Also controls how long to wait for a missing frame before skipping ahead.
   * Default: 80 (2 frames at 25fps)
   */
  maxBufferDelayMs?: number;

  /**
   * Provider name for telemetry.
   */
  providerName?: string;
}

/**
 * Jitter buffer state machine.
 * @internal
 */
type BufferState = 'direct' | 'filling' | 'draining' | 'starved';

/**
 * A frame held in the jitter buffer awaiting playback.
 * @internal
 */
interface BufferedFrame {
  protobufData: ArrayBuffer;
  seq: number;
  receivedAt: number;
  isRecovered: boolean;
}

interface JitterStatsCounters {
  jitterDropStale: number;
  jitterDropLate: number;
  jitterDropOverflow: number;
  jitterPruneStale: number;
  jitterSkipEvents: number;
  jitterSkipFrames: number;
  jitterStarved: number;
  jitterNoInOrder: number;
  jitterOutOfOrderRenderRejects: number;
}

/**
 * One run of drain cycles that rendered nothing while waiting for a missing
 * frame — what the viewer perceives as the picture freezing.
 * @internal
 */
interface StallRun {
  /** Sequence the drain loop was waiting on. */
  seq: number;
  /** Monotonic start of the freeze. */
  startedAt: number;
  /**
   * Monotonic end of the freeze, set when it is closed. The duration is
   * `endedAt - startedAt`.
   *
   * Duration used to be derived as `ticks × 40ms`, which measured how many
   * drain cycles elapsed rather than how long the picture was actually frozen.
   * setTimeout only guarantees *at least* its delay — measured drift here ran
   * 18–58ms per cycle — so that figure understated real freezes and could only
   * ever land on multiples of 40. It also could not describe a freeze at all
   * when the buffer ran dry, because the drain loop stops rescheduling itself
   * and no cycles elapse.
   */
  endedAt: number;
  /**
   * Drain cycles that spun without rendering. Descriptive only — never used to
   * compute duration. Zero for a freeze that began with an empty buffer, since
   * the loop is not running then.
   */
  ticks: number;
  /** True when the freeze began because the buffer was empty. */
  starved: boolean;
}

/**
 * Playback quality for one round: what reached the screen, as opposed to the
 * jitter counters which describe what reached the buffer.
 * @internal
 */
interface QualityStats {
  /** Frames the start transition was asked to produce. */
  startTransitionExpected: number;
  /** Frames of the start transition actually rendered. */
  startTransitionRendered: number;
  /** Frames the end transition was asked to produce. */
  endTransitionExpected: number;
  /** Frames of the end transition actually rendered. */
  endTransitionRendered: number;
  /**
   * Wall-clock boundaries of the two transitions, so the trace can lay the
   * round out as one continuous timeline instead of a few isolated markers.
   * Zero while the corresponding transition has not run.
   */
  startTransitionBeganAt: number;
  startTransitionEndedAt: number;
  endTransitionBeganAt: number;
  /** Completed stall runs. */
  stalls: StallRun[];
  /** Stall run in progress, if the drain loop is currently spinning. */
  openStall: StallRun | null;
  /** Sequence ranges that were skipped without ever rendering. */
  skipped: Array<{ from: number; to: number; at: number }>;
  /** How the round ended. */
  endReason: 'transition' | 'idle_packet' | 'stall_timeout' | 'disconnect' | '';
  /**
   * Whether the frame carried by the transition-end packet is the one last
   * rendered. False means the round's final frames never reached the screen.
   * Null when no transition-end packet arrived.
   */
  tailIntact: boolean | null;
  /** Frames still queued when the round ended; these are discarded unrendered. */
  tailDiscarded: number;
}

function createEmptyQualityStats(): QualityStats {
  return {
    startTransitionExpected: 0,
    startTransitionRendered: 0,
    endTransitionExpected: 0,
    endTransitionRendered: 0,
    startTransitionBeganAt: 0,
    startTransitionEndedAt: 0,
    endTransitionBeganAt: 0,
    stalls: [],
    openStall: null,
    skipped: [],
    endReason: '',
    tailIntact: null,
    tailDiscarded: 0,
  };
}

function createEmptyJitterStats(): JitterStatsCounters {
  return {
    jitterDropStale: 0,
    jitterDropLate: 0,
    jitterDropOverflow: 0,
    jitterPruneStale: 0,
    jitterSkipEvents: 0,
    jitterSkipFrames: 0,
    jitterStarved: 0,
    jitterNoInOrder: 0,
    jitterOutOfOrderRenderRejects: 0,
  };
}

/**
 * AnimationHandler manages all animation playback and transition logic.
 *
 * @internal
 * This class is used internally by AvatarPlayer.
 * Applications should not instantiate this class directly.
 */
export class AnimationHandler {
  /** @internal */
  private renderer: AvatarRenderer;
  /** @internal */
  private config: Required<
    Omit<AnimationHandlerConfig, 'onStreamStalled' | 'providerName'>
  >;

  // Frame tracking
  /** @internal */
  private animationFrameCount = 0;
  /** @internal */
  private lastRenderedFrameSeq = -1;
  /** @internal */
  private renderedFrameCount = 0;

  // Transition state
  /** @internal */
  private isPlayingTransition = false;
  /** @internal */
  private isTransitioningToIdle = false;
  /** @internal */
  private transitionFrames: AnimationFrame[] = [];
  /** @internal */
  private transitionFrameIndex = 0;

  /**
   * Frame count of an end transition that has been booked but not yet started,
   * or null when none is pending.
   * @internal
   */
  private pendingEndTransitionFrames: number | null = null;
  /**
   * Payload of the booked end transition's packet, held so the tail check runs
   * against the frame that actually ended up on screen.
   * @internal
   */
  private pendingEndTransitionTail: Uint8Array | null = null;
  /**
   * Playback slots still owed to the queued frames before the end transition
   * may take over — the jitter buffer's initial fill, repaid at the end of the
   * round. Counted down one slot per tick.
   * @internal
   */
  private endTransitionSlotsOwed = 0;

  // Guards against race conditions during async transition generation
  /** @internal */
  private isGeneratingStartTransition = false;
  /** @internal */
  private isGeneratingEndTransition = false;

  // Session-level flags to prevent duplicate handling
  // These are reset in resetTracking() for new sessions
  /** @internal */
  private hasHandledTransitionStart = false;
  /** @internal */
  private hasHandledTransitionEnd = false;

  // Watchdog for detecting stalled data stream
  /** @internal */
  private lastFrameReceivedTime = 0;
  /** @internal */
  private isInSession = false;
  /** @internal */
  private watchdogTimer: ReturnType<typeof setInterval> | null = null;
  /** @internal */
  private hasReportedStall = false;
  /** @internal */
  private static readonly STALL_TIMEOUT_MS = 5000;
  /** @internal */
  private onStreamStalledCallback: (() => void) | null = null;
  /** @internal */
  private providerName: string = '';
  /** @internal */
  private isStalledFallback = false; // True when fallback to idle was triggered due to stall
  /**
   * True while the avatar is already showing idle. The server keeps sending
   * idle packets while the avatar is silent, and each one used to re-trigger
   * `renderFrame(undefined, true)` — which resets AvatarKit's idle frame index
   * back to 0, snapping the pose mid-loop (a visible twitch, most obvious right
   * after connect). Starts true because the avatar is idle before any RTC data
   * arrives.
   * @internal
   */
  private isIdle = true;

  // Playback stats
  /** @internal */
  private playbackStatsTimer: ReturnType<typeof setInterval> | null = null;
  /** @internal */
  private playbackFrameCount = 0;
  /** @internal */
  private playbackFrameTimestamps: number[] = [];
  /** @internal */
  private playbackSkipCount = 0;
  /** @internal */
  private playbackExpectedSeq = -1;

  // Jitter buffer
  /** @internal */
  private bufferState: BufferState = 'direct';
  /** @internal */
  private frameBuffer = new Map<number, BufferedFrame>();
  /** @internal */
  private bufferNextSeq = -1;
  /**
   * The single playback clock. One timer paces everything that reaches the
   * screen during a round — transition frames and buffered stream frames
   * alike — because both run at the same rate and are two halves of one
   * continuous sequence.
   *
   * They used to be paced by two independent timers with no mutual exclusion,
   * each free to fire whenever its own grid came due. Nothing coordinated the
   * hand-over, so the start transition's last frame and the buffer's first
   * could land arbitrarily close together: measured at 28ms, against a 40ms
   * interval. Both timers being armed at once also meant a transition running
   * alongside a draining buffer had two independent writers to the renderer
   * for its whole duration.
   * @internal
   */
  private playbackTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Monotonic target time of the slot most recently filled. The next slot is
   * due one interval later.
   *
   * Frames are paced against this fixed grid rather than by waiting a flat
   * interval after each render: setTimeout only guarantees *at least* its
   * delay — measured drift ran 18–58ms per cycle — so a flat wait lets every
   * late callback push the whole sequence further out and the error compounds.
   * Deriving each delay from `previous target + interval` absorbs a late
   * callback into the next wait instead, keeping the error bounded.
   * @internal
   */
  private playbackGridSlot = 0;
  /** @internal */
  private static readonly BUFFER_MAX_SIZE = 4;
  /** @internal */
  private static readonly BUFFER_INITIAL_FILL = 2;
  /**
   * The playback grid: 25fps, one slot every 40ms. Locally generated
   * transition frames and streamed animation frames share it, so a transition
   * hands over to (or takes over from) the stream without a change of pace.
   * @internal
   */
  private static readonly FRAME_INTERVAL_MS = 40;

  // Cumulative session stats (never reset until getSessionSummary)
  /** @internal */
  private cumulativeTotalFrames = 0;
  /** @internal */
  private cumulativeSkipped = 0;
  /** @internal */
  private cumulativeRecovered = 0;
  /** @internal */
  private cumulativeDropped = 0;
  /** @internal */
  /**
   * Playback time accumulated across the session's rounds, in ms. Paired with
   * cumulativeTotalFrames to give a session frame rate by the same definition
   * used per round: frames divided by the time they played over. Idle time
   * between rounds is excluded, since no frames are due then.
   * @internal
   */
  private cumulativePlaybackMs = 0;
  /** @internal */
  private cumulativeJitterStats = createEmptyJitterStats();

  // Per-conversation stats for rtc_playback_stats
  /** @internal */
  private conversationFrameCount = 0;
  /** @internal */
  private conversationSkipped = 0;
  /** @internal */
  private conversationRecovered = 0;
  /** @internal */
  private conversationDropped = 0;
  /** @internal */
  private conversationStartTime = 0;
  /**
   * Round start on the monotonic clock, paired with conversationStartTime.
   * All in-round marks are collected on performance.now() and mapped onto the
   * wall clock through this anchor when spans are emitted.
   * @internal
   */
  private conversationStartMono = 0;
  /** @internal */
  private conversationJitterStats = createEmptyJitterStats();
  /**
   * Correlation id for the current playback round, generated locally when the
   * round starts. Nothing in the RTC stream carries a request id — the agent
   * owns the driving session — so this exists purely to tie this SDK's own
   * telemetry for one round together (events, metrics, trace).
   * @internal
   */
  private conversationId = '';
  /**
   * The W3C traceparent the server stamped on this round's animation, or
   * undefined before one has been seen.
   *
   * Over RTC the round's id is minted locally and never travels upstream, so a
   * trace_id derived from it has no relation to the server's and one round
   * reports as two unrelated traces. This is the only thing joining them.
   *
   * First non-empty wins: every packet of a round carries the same value, so
   * pinning the first keeps the trace_id stable even if later ones omit it.
   * Cleared per round — carrying one over would file the next round under the
   * previous round's trace.
   */
  private serverTraceparent: string | undefined;
  /**
   * Guards against reporting the same round twice: the normal ending and the
   * fallback endings can both fire for one round.
   * @internal
   */
  private hasReportedConversation = false;
  /**
   * Throttles the starved metric to one record per round.
   * @internal
   */
  private hasEmittedStarvedMetric = false;
  /**
   * Per-round playback quality tracking. The counters above cover what was
   * received; these cover what the viewer actually saw — how much of each
   * transition played, where the drain loop spun without rendering, which
   * sequences never made it to screen, and whether the round's tail survived.
   * @internal
   */
  private conversationQuality = createEmptyQualityStats();
  /**
   * Payload of the most recently rendered speaking frame, kept so the
   * transition-end packet can be compared against it to detect a truncated
   * tail. Holds one frame (well under a kilobyte) and is cleared per round.
   * @internal
   */
  private lastRenderedPayload: Uint8Array | null = null;

  /**
   * @internal
   */
  constructor(renderer: AvatarRenderer, config: AnimationHandlerConfig = {}) {
    this.renderer = renderer;
    this.config = {
      transitionStartFrameCount: config.transitionStartFrameCount ?? 8,
      transitionEndFrameCount: config.transitionEndFrameCount ?? 40,
      enableJitterBuffer: config.enableJitterBuffer ?? true,
      maxBufferDelayMs: config.maxBufferDelayMs ?? 80,
    };
    this.onStreamStalledCallback = config.onStreamStalled ?? null;
    this.providerName = config.providerName ?? '';
  }

  /**
   * Handle animation data received from RTC provider.
   * @param protobufData - Raw protobuf bytes
   * @param frameSeq - Frame sequence number (optional)
   * @param isRecovered - Whether this frame was recovered via ALR
   * @internal
   */
  handleAnimationData(
    protobufData: ArrayBuffer,
    frameSeq?: number,
    isRecovered?: boolean,
  ): void {
    // A start transition is the run-up into this very stream: cutting it short
    // the instant the first frame lands is what kept it from ever finishing —
    // the frames arrive well before it has played out, so it was routinely
    // stopped a third of the way through and the picture jumped into speech.
    // Let it play; these frames buffer and the drain loop picks them up when it
    // ends, which is exactly the hand-over the extra transition frames were
    // sized for.
    //
    // An end transition is a different matter: it is winding down toward idle,
    // and a streamed frame means a new round has begun, so it must give way.
    if (this.isPlayingTransition && this.isTransitioningToIdle) {
      this.stopTransition();
    }

    // Update watchdog state
    const now = Date.now();
    if (this.hasReportedStall) {
      // Recovered from stall
      const stallDuration = now - this.lastFrameReceivedTime;
      logger.info(
        'AnimationHandler',
        `Data stream resumed after ${stallDuration}ms stall`,
      );
      this.hasReportedStall = false;
    }
    // If we were in stalled fallback, clear the flag (data has resumed, no transition needed)
    if (this.isStalledFallback) {
      logger.info(
        'AnimationHandler',
        'Resuming from stall fallback, rendering directly without transition',
      );
      this.isStalledFallback = false;
    }
    this.lastFrameReceivedTime = now;

    this.animationFrameCount++;

    // In lossy networks the transition packet may be delayed/lost.
    // Ensure session-level watchdog/stats still start on first valid playback frame.
    this.ensureSessionActive(frameSeq);

    // Once per round: the value is identical on every packet, and decoding the
    // message again for each one would parse the whole round twice.
    // Kept trying until one is found rather than only inspecting the first
    // packet: the round may open with frames the server stamped nothing on, and
    // giving up after one would lose the trace for the whole round. Once found
    // the decode stops, so the steady state is one parse per round.
    if (this.serverTraceparent === undefined) {
      this.serverTraceparent = peekTraceparent(new Uint8Array(protobufData));
    }

    // Animation data means the avatar is speaking again — leave idle here
    // rather than at the render call, so the jitter-buffer path (which only
    // enqueues and renders later, asynchronously) clears the flag too.
    this.isIdle = false;

    // Jitter buffer path: insert into buffer instead of rendering directly
    if (this.config.enableJitterBuffer && frameSeq !== undefined) {
      this.bufferFrame(protobufData, frameSeq, isRecovered ?? false);
      return;
    }

    // Direct path (no buffer)
    // Check for out-of-order delivery
    if (frameSeq !== undefined && this.lastRenderedFrameSeq !== -1) {
      if (frameSeq < this.lastRenderedFrameSeq) {
        // Out-of-order frame - discard (already rendered a later frame)
        logger.warn(
          'AnimationHandler',
          `OUT-OF-ORDER: seq=${frameSeq}, lastRendered=${this.lastRenderedFrameSeq}${isRecovered ? ' [RECOVERED]' : ''}, discarding`,
        );
        this.conversationDropped++;
        return;
      } else if (frameSeq === this.lastRenderedFrameSeq) {
        // Duplicate frame - skip silently (expected when server sends redundant frames)
        return;
      } else if (frameSeq > this.lastRenderedFrameSeq + 1) {
        const gap = frameSeq - this.lastRenderedFrameSeq - 1;
        logger.warn(
          'AnimationHandler',
          `GAP: ${gap} frame(s) between ${this.lastRenderedFrameSeq} and ${frameSeq}${isRecovered ? ' [RECOVERED]' : ''}`,
        );
      }
    }

    if (frameSeq !== undefined) {
      this.lastRenderedFrameSeq = frameSeq;
    }

    // Render directly (decoding happens inside the renderer)
    this.renderedFrameCount++;
    if (isRecovered) this.conversationRecovered++;
    this.renderer.renderFromProtobuf(protobufData);
    this.lastRenderedPayload = new Uint8Array(protobufData);
    this.logRenderedFrame('direct', frameSeq, isRecovered);

    // Collect playback stats
    this.playbackFrameTimestamps.push(performance.now());
    this.playbackFrameCount++;
    this.conversationFrameCount++;
    if (frameSeq !== undefined) {
      if (
        this.playbackExpectedSeq >= 0 &&
        frameSeq > this.playbackExpectedSeq
      ) {
        const gap = frameSeq - this.playbackExpectedSeq;
        this.playbackSkipCount += gap;
        this.conversationSkipped += gap;
      }
      this.playbackExpectedSeq = frameSeq + 1;
    }
  }

  /**
   * Handle transition packet - generate and play transition from idle to target.
   * Only starts transition on the first packet; subsequent packets are ignored while transitioning.
   * @param protobufData - Protobuf data containing target frame
   * @param frameCount - Number of transition frames (overrides config if provided)
   * @internal
   */
  async handleTransitionData(
    protobufData: ArrayBuffer,
    frameCount?: number,
  ): Promise<void> {

    logger.info(
      'AnimationHandler',
      `Start transition packet received (bytes=${protobufData.byteLength}, requestedFrames=${frameCount ?? this.config.transitionStartFrameCount}, hasHandledStart=${this.hasHandledTransitionStart}, isInSession=${this.isInSession}, isPlayingTransition=${this.isPlayingTransition}, isGeneratingStart=${this.isGeneratingStartTransition}, lastRenderedSeq=${this.lastRenderedFrameSeq}, bufferState=${this.bufferState}, buffered=${this.frameBuffer.size})`,
    );

    // A start-transition packet that arrives while we're still playing the
    // previous conversation's end transition (speak→idle) is the next
    // conversation beginning. Don't drop it as "late": stop the end transition
    // and reset session tracking so this start transition plays normally.
    // generateTransitionToFrame anchors on the current rendered frame, so it
    // smoothly continues from wherever the end transition was.
    if (this.isPlayingTransition && this.isTransitioningToIdle) {
      this.stopTransition();
      this.resetTracking();
    }

    // Ignore if we've already handled a transition start for this session
    if (this.hasHandledTransitionStart) {
      return;
    }
    // Ignore if already playing or generating a start transition
    if (this.isPlayingTransition && !this.isTransitioningToIdle) {
      return;
    }
    if (this.isGeneratingStartTransition) {
      return;
    }

    if (!this.renderer.isReady()) {
      logger.warn('AnimationHandler', 'Renderer not ready for transition');
      return;
    }

    // Once streaming playback has started, start-transition packets are stale.
    // Ignore them to avoid jumping back into transition and causing visual jitter.
    if (
      this.isInSession &&
      (this.lastRenderedFrameSeq >= 0 ||
        this.frameBuffer.size > 0 ||
        this.bufferState !== 'direct')
    ) {
      this.hasHandledTransitionStart = true;
      logger.warn(
        'AnimationHandler',
        `Ignoring late transition packet after playback start (lastRenderedSeq=${this.lastRenderedFrameSeq}, bufferState=${this.bufferState}, buffered=${this.frameBuffer.size})`,
      );
      return;
    }

    // Mark that we've handled transition start for this session
    this.hasHandledTransitionStart = true;
    // Reset transition end flag since we're starting a new transition
    this.hasHandledTransitionEnd = false;

    // Start session and watchdog
    this.ensureSessionActive();

    // One extra frame covers the jitter buffer's initial fill. Draining starts
    // as soon as BUFFER_INITIAL_FILL frames are held, and the first of those
    // arrives while the transition is still running — so the wait is for the
    // second one, a single frame interval. That interval used to be spent on a
    // still picture between the transition ending and playback starting;
    // extending the transition across it fills the same stretch with motion,
    // and the round does not start any later.
    const frames =
      (frameCount ?? this.config.transitionStartFrameCount) +
      (AnimationHandler.BUFFER_INITIAL_FILL - 1);
    this.conversationQuality.startTransitionExpected = frames;
    logger.info(
      'AnimationHandler',
      `Generating ${frames} transition frames to target`,
    );

    // Set guard flag before async operation
    this.isGeneratingStartTransition = true;

    try {
      // Generate one frame more than will be played, then drop the last one.
      //
      // The generator forces its final frame to the exact target it was given,
      // and that target is the frame the stream then opens with. Playing the
      // full sequence therefore puts that frame on screen twice — once ending
      // the transition, once opening playback — holding the picture still for
      // an extra interval right where speech begins. Dropping it lands the
      // transition one step short, so the stream's first frame continues the
      // motion instead of repeating it.
      //
      // The extra frame's worth of time is not lost: the jitter buffer fills
      // during the transition, so the interval that used to sit empty between
      // the transition ending and the buffer reaching its initial depth is now
      // spent playing.
      const generated = await this.renderer.generateTransitionToFrame(
        protobufData,
        frames + 1,
      );
      const transitionFrames = generated.slice(0, -1);
      logger.info(
        'AnimationHandler',
        `Generated ${transitionFrames.length} transition frames`,
      );

      // Start playing transition frames at 25fps
      this.isPlayingTransition = true;
      this.isTransitioningToIdle = false;
      this.transitionFrames = transitionFrames;
      this.transitionFrameIndex = 0;

      // Takes the current instant as the first slot; the stream's frames then
      // fall on the same grid when the buffer takes over.
      this.startPlaybackClock();
    } catch (error) {
      logger.error('AnimationHandler', 'Failed to generate transition:', error);
      // Fallback: render protobuf directly
      this.renderer.renderFromProtobuf(protobufData);
      this.logRenderedFrame('transition-fallback');
    } finally {
      this.isGeneratingStartTransition = false;
    }
  }

  /**
   * Handle transition end - generate and play reverse transition back to idle.
   * Only starts transition on the first packet; subsequent packets are ignored while transitioning.
   * @param protobufData - Protobuf data containing last animation frame
   * @param frameCount - Number of transition frames (overrides config if provided)
   * @internal
   */
  async handleTransitionToIdle(
    protobufData: ArrayBuffer,
    frameCount?: number,
  ): Promise<void> {
    // Ignore stale transition-end packets after session has already returned to idle.
    if (!this.isInSession) {
      logger.info(
        'AnimationHandler',
        'Ignoring transition end packet with no active session',
      );
      return;
    }

    // Ignore if we've already handled a transition end for this session
    if (this.hasHandledTransitionEnd) {
      return;
    }
    // Ignore if already playing or generating an end transition
    if (this.isPlayingTransition && this.isTransitioningToIdle) {
      return;
    }
    if (this.isGeneratingEndTransition) {
      return;
    }

    if (!this.renderer.isReady()) {
      logger.warn(
        'AnimationHandler',
        'Renderer not ready for transition to idle',
      );
      this.isIdle = true;
      this.renderer.renderFrame(undefined, true);
      this.logRenderedFrame('idle');
      return;
    }

    // Mark that we've handled transition end for this session
    this.hasHandledTransitionEnd = true;

    this.conversationQuality.endReason = 'transition';

    // The jitter buffer's initial fill is a debt that has to be repaid here.
    //
    // Playback holds the first arriving frame back until BUFFER_INITIAL_FILL
    // frames are queued, so every frame goes on screen that much later than the
    // server sent it — the driving service knows this and delays the audio to
    // match. What it does not do is delay this packet: it is sent as soon as the
    // last animation frame goes out, as if playback were already finished. It is
    // not; the frames covering that initial fill are still queued, and their
    // audio is still playing. Cutting to the end transition here would take the
    // picture into idle while the round is still being spoken.
    //
    // So let the clock run out the debt first — the same count the opening
    // transition was extended by, for the same reason — and only then hand over.
    const owed = AnimationHandler.BUFFER_INITIAL_FILL - 1;
    this.pendingEndTransitionFrames =
      frameCount ?? this.config.transitionEndFrameCount;
    this.pendingEndTransitionTail = new Uint8Array(protobufData);
    this.endTransitionSlotsOwed = owed;

    // Nothing is pacing playback (the buffer starved, or it never filled), so no
    // future slot is coming to run the debt down — hand over immediately.
    if (owed <= 0 || this.playbackTimer === null) {
      await this.beginEndTransition();
    }
  }

  /**
   * Generate the end transition and hand it to the clock.
   * @internal
   */
  private async beginEndTransition(): Promise<void> {
    const frames = this.pendingEndTransitionFrames;
    if (frames === null || this.isGeneratingEndTransition) {
      return;
    }
    this.pendingEndTransitionFrames = null;
    this.endTransitionSlotsOwed = 0;

    // The debt has been repaid, so whatever is on screen now is the round's
    // last spoken frame. Comparing the packet against it says whether the tail
    // made it — nothing else can, since loss is detected from a gap to the
    // *following* frame and the final frame has none.
    const tail = this.pendingEndTransitionTail;
    this.pendingEndTransitionTail = null;
    if (tail) {
      this.conversationQuality.tailIntact = this.isSameFrameAsLastRendered(tail);
    }
    // Anything still queued past the repaid slots is genuinely dropped.
    this.conversationQuality.tailDiscarded = this.frameBuffer.size;

    // Drop the leftovers so they cannot interleave with the transition.
    this.flushBuffer();

    this.conversationQuality.endTransitionExpected = frames;
    logger.info(
      'AnimationHandler',
      `Generating ${frames} transition frames to idle`,
    );

    // Set guard flag before async operation
    this.isGeneratingEndTransition = true;

    try {
      const transitionFrames = await this.renderer.generateTransitionToIdle(frames);

      this.isPlayingTransition = true;
      this.isTransitioningToIdle = true;
      this.transitionFrames = transitionFrames;
      this.transitionFrameIndex = 0;

      // Resume the grid the stream was on rather than re-anchoring to this
      // instant. Generation takes time, and starting the clock afresh when it
      // finishes put the transition's opening frame wherever that landed —
      // 7-13ms after the last stream frame, against a 40ms interval.
      this.startPlaybackClock(true);
    } catch (error) {
      logger.error(
        'AnimationHandler',
        'Failed to generate reverse transition:',
        error,
      );
      this.flushBuffer();
      this.isIdle = true;
      this.renderer.renderFrame(undefined, true);
      this.logRenderedFrame('idle');
    } finally {
      this.isGeneratingEndTransition = false;
    }
  }

  /**
   * Start idle animation.
   * @internal
   */
  startIdle(): void {
    // Every route to idle lands here. A transition-end packet has already
    // recorded its reason; anything still unset arrived another way — the
    // watchdog giving up, or a bare idle packet with no end transition, which
    // cuts straight to idle with no blend and is what a viewer sees as a jump.
    if (!this.conversationQuality.endReason) {
      this.conversationQuality.endReason = this.isStalledFallback
        ? 'stall_timeout'
        : 'idle_packet';
      this.conversationQuality.tailDiscarded = this.frameBuffer.size;
    }
    this.closeStallRun();
    this.reportConversationStats();
    this.isInSession = false;
    this.hasReportedStall = false;

    // Already idle — do not re-enter. Re-rendering idle resets AvatarKit's idle
    // frame index to 0, which snaps the pose mid-loop instead of leaving the
    // running idle animation untouched.
    if (this.isIdle) {
      return;
    }

    this.isIdle = true;
    this.renderer.renderFrame(undefined, true);
    this.logRenderedFrame('idle');
  }

  /**
   * Ensure session-level timers/stats are active.
   * @internal
   */
  private ensureSessionActive(frameSeq?: number): void {
    if (this.isInSession) {
      return;
    }

    this.isInSession = true;
    this.lastFrameReceivedTime = Date.now();
    this.hasReportedStall = false;
    if (!this.conversationStartTime) {
      this.conversationStartTime = Date.now();
      this.conversationStartMono = performance.now();
      // Same guard as the start time: a round may be entered from the start
      // transition or, on a lossy network, from the first animation frame —
      // whichever comes first mints the id, and the other must not replace it.
      this.conversationId = generateConversationId();
    }
    this.startWatchdog();
    this.startPlaybackStats();

    if (frameSeq !== undefined) {
      logger.info(
        'AnimationHandler',
        `Session started from animation frame seq=${frameSeq}`,
      );
    }
  }

  /**
   * Reset animation frame tracking (call on session start).
   * @internal
   */
  resetTracking(): void {
    this.lastRenderedFrameSeq = -1;
    this.renderedFrameCount = 0;
    this.animationFrameCount = 0;
    // Reset session-level transition flags for new session
    this.hasHandledTransitionStart = false;
    this.hasHandledTransitionEnd = false;
    // A booking that never got its slots must not fire into the next round.
    this.pendingEndTransitionFrames = null;
    this.pendingEndTransitionTail = null;
    this.endTransitionSlotsOwed = 0;
    this.resetPlaybackStats();
    this.flushBuffer();
    logger.info('AnimationHandler', 'Frame tracking reset');
  }

  /**
   * Check if currently playing transition frames.
   * @internal
   */
  isInTransition(): boolean {
    return (
      this.isPlayingTransition ||
      this.isGeneratingStartTransition ||
      this.isGeneratingEndTransition
    );
  }

  /**
   * Stop transition playback.
   * @internal
   */
  stopTransition(): void {
    this.isPlayingTransition = false;
    this.isTransitioningToIdle = false;
    this.isGeneratingStartTransition = false;
    this.isGeneratingEndTransition = false;
    // Note: We intentionally do NOT reset hasHandledTransitionStart/End here
    // Those are session-level flags, only reset in resetTracking()
    this.transitionFrames = [];
    this.transitionFrameIndex = 0;
    // A booking still waiting out its slots dies with the transition it belongs
    // to; leaving it set would drop an end transition into the next round.
    this.pendingEndTransitionFrames = null;
    this.pendingEndTransitionTail = null;
    this.endTransitionSlotsOwed = 0;
    // Clears the buffer and, with the transition flags already down, stops the
    // playback clock.
    this.flushBuffer();
  }

  /**
   * Get cumulative session summary for telemetry reporting.
   * @internal
   */
  getSessionSummary(): {
    totalFrames: number;
    totalSkipped: number;
    totalRecovered: number;
    totalDropped: number;
    avgFps: number;
    jitterDropStale: number;
    jitterDropLate: number;
    jitterDropOverflow: number;
    jitterPruneStale: number;
    jitterSkipEvents: number;
    jitterSkipFrames: number;
    jitterStarved: number;
    jitterNoInOrder: number;
    jitterOutOfOrderRenderRejects: number;
  } {
    const avgFps =
      this.cumulativePlaybackMs > 0
        ? Math.round(
            (this.cumulativeTotalFrames * 1000) / this.cumulativePlaybackMs,
          )
        : 0;

    return {
      totalFrames: this.cumulativeTotalFrames,
      totalSkipped: this.cumulativeSkipped,
      totalRecovered: this.cumulativeRecovered,
      totalDropped: this.cumulativeDropped,
      avgFps,
      jitterDropStale: this.cumulativeJitterStats.jitterDropStale,
      jitterDropLate: this.cumulativeJitterStats.jitterDropLate,
      jitterDropOverflow: this.cumulativeJitterStats.jitterDropOverflow,
      jitterPruneStale: this.cumulativeJitterStats.jitterPruneStale,
      jitterSkipEvents: this.cumulativeJitterStats.jitterSkipEvents,
      jitterSkipFrames: this.cumulativeJitterStats.jitterSkipFrames,
      jitterStarved: this.cumulativeJitterStats.jitterStarved,
      jitterNoInOrder: this.cumulativeJitterStats.jitterNoInOrder,
      jitterOutOfOrderRenderRejects:
        this.cumulativeJitterStats.jitterOutOfOrderRenderRejects,
    };
  }

  /**
   * Report per-conversation playback stats via telemetry.
   * Called when a conversation ends (transition to idle).
   * @internal
   */
  private reportConversationStats(): void {
    const hasPlaybackFrames = this.conversationFrameCount > 0;
    const hasJitterStats = Object.values(this.conversationJitterStats).some(
      (value) => value > 0,
    );
    // A round that started still reports even with nothing to show for it: a
    // round that played zero frames is a total failure, and staying silent
    // would make the worst outcome the one least visible in the data. Rounds
    // that never started (no id) are not rounds at all.
    const roundStarted = this.conversationId !== '';
    if (!roundStarted && !hasPlaybackFrames && !hasJitterStats) return;
    // Both the normal ending (end transition finished) and the abnormal ones
    // (stall fallback, bare idle packet) reach this point, and a round can pass
    // through more than one of them — report only the first.
    if (this.hasReportedConversation) return;
    this.hasReportedConversation = true;

    const durationMs = this.conversationStartTime
      ? Date.now() - this.conversationStartTime
      : 0;
    // Rate of the streamed frames, measured over the stretch they played on.
    //
    // frame_count covers streamed frames only — the locally generated
    // transition frames are rendered on their own path and never reach these
    // counters — so the span has to exclude the transitions too. duration_ms
    // spans the whole round, transitions included, and dividing by that put
    // 28 frames' worth of time (1.1s of an 8+20 frame transition pair) in the
    // denominator with nothing in the numerator: a healthy 25fps round
    // reported 14.
    const speakingMs =
      this.conversationQuality.startTransitionEndedAt &&
      this.conversationQuality.endTransitionBeganAt
        ? this.conversationQuality.endTransitionBeganAt -
          this.conversationQuality.startTransitionEndedAt
        : durationMs;
    const avgFps =
      speakingMs > 0
        ? Math.round((this.conversationFrameCount * 1000) / speakingMs)
        : 0;
    const totalExpected = this.conversationFrameCount + this.conversationSkipped;
    // Whole percent: the metrics backend cannot store fractional values, so
    // ratios are rounded here and precise figures are recovered from the
    // integer numerator/denominator that ship alongside.
    const skipRate =
      totalExpected > 0
        ? Math.round((this.conversationSkipped / totalExpected) * 100)
        : 0;

    const quality = this.conversationQuality;
    const stallTotalMs = quality.stalls.reduce(
      (sum, run) => sum + AnimationHandler.stallRunDurationMs(run),
      0,
    );
    const stallMaxMs = quality.stalls.reduce(
      (max, run) => Math.max(max, AnimationHandler.stallRunDurationMs(run)),
      0,
    );
    // Share of the round the picture spent frozen. Computed here rather than
    // left to the backend: dividing an aggregated stall metric by an aggregated
    // duration metric divides one distribution's P95 by another's, which
    // answers nothing. A ratio is only meaningful while both numbers still
    // belong to the same round. Rounded like skipRate above, for the same
    // reason.
    const stallRate =
      durationMs > 0 ? Math.round((stallTotalMs / durationMs) * 100) : 0;

    // Variable-length detail travels as JSON strings: the log backend flattens
    // nested structures, and a flattened array of objects is rejected outright.
    const stallEventsJson = JSON.stringify(
      quality.stalls.map((run) => ({
        seq: run.seq,
        ms: AnimationHandler.stallRunDurationMs(run),
        ticks: run.ticks,
        // Whether the buffer was empty or merely missing the next sequence.
        // Both freeze the picture; only the first means nothing was arriving.
        starved: run.starved,
      })),
    );
    const skippedSeqsJson = JSON.stringify(
      quality.skipped.map((range) =>
        range.from === range.to ? `${range.from}` : `${range.from}-${range.to}`,
      ),
    );



    trackEvent('rtc_playback_stats', 'info', {
      provider: this.providerName,
      conversation_id: this.conversationId,
      avg_fps: avgFps,
      frame_count: this.conversationFrameCount,
      skipped_frames: this.conversationSkipped,
      skip_rate: skipRate,
      duration_ms: durationMs,
      // How the round ended, and whether its final frames reached the screen.
      end_reason: quality.endReason,
      tail_intact: quality.tailIntact,
      tail_discarded: quality.tailDiscarded,
      // Transitions: expected versus what actually played. A shortfall on the
      // start transition is the visible jump into speech.
      start_transition_expected: quality.startTransitionExpected,
      start_transition_rendered: quality.startTransitionRendered,
      end_transition_expected: quality.endTransitionExpected,
      end_transition_rendered: quality.endTransitionRendered,
      // Freezes: drain cycles that rendered nothing while awaiting a frame.
      stall_count: quality.stalls.length,
      stall_total_ms: stallTotalMs,
      stall_max_ms: stallMaxMs,
      stall_rate: stallRate,
      stall_events: stallEventsJson,
      // Sequences abandoned without ever rendering.
      skipped_seqs: skippedSeqsJson,
    });

    this.recordPlaybackMetrics({
      durationMs,
      avgFps,
      skipRate,
      stallTotalMs,
      stallMaxMs,
      stallRate,
    });
    this.emitPlaybackTrace(durationMs, stallTotalMs);

    // Accumulate into session-level stats
    this.cumulativeTotalFrames += this.conversationFrameCount;
    this.cumulativeSkipped += this.conversationSkipped;
    this.cumulativeRecovered += this.conversationRecovered;
    this.cumulativeDropped += this.conversationDropped;
    this.cumulativePlaybackMs += speakingMs;
    this.mergeJitterStats(
      this.cumulativeJitterStats,
      this.conversationJitterStats,
    );

    // Reset per-conversation counters
    this.conversationFrameCount = 0;
    this.conversationSkipped = 0;
    this.conversationRecovered = 0;
    this.conversationDropped = 0;
    this.conversationStartTime = 0;
    this.conversationStartMono = 0;
    this.conversationId = '';
    this.serverTraceparent = undefined;
    this.conversationJitterStats = createEmptyJitterStats();
    this.conversationQuality = createEmptyQualityStats();
    this.lastRenderedPayload = null;
    this.hasReportedConversation = false;
    this.hasEmittedStarvedMetric = false;
  }

  /**
   * Clean up resources.
   * @internal
   */
  dispose(): void {
    this.stopTransition();
    this.stopWatchdog();
  }

  /**
   * Start the watchdog timer to detect stalled data streams.
   * @internal
   */
  private startWatchdog(): void {
    if (this.watchdogTimer) {
      return; // Already running
    }

    this.watchdogTimer = setInterval(() => {
      if (!this.isInSession) {
        return;
      }

      // Don't check during transition playback (we're playing generated frames, not receiving)
      if (this.isPlayingTransition) {
        return;
      }

      const elapsed = Date.now() - this.lastFrameReceivedTime;
      if (
        elapsed > AnimationHandler.STALL_TIMEOUT_MS &&
        !this.hasReportedStall
      ) {
        logger.error(
          'AnimationHandler',
          `Data stream stalled: no frames received for ${elapsed}ms, falling back to idle`,
        );
        this.hasReportedStall = true;
        this.isStalledFallback = true;

        // Trigger fallback to idle
        this.startIdle();

        // Notify external listener
        if (this.onStreamStalledCallback) {
          try {
            this.onStreamStalledCallback();
          } catch (e) {
            logger.error(
              'AnimationHandler',
              'Error in onStreamStalled callback:',
              e,
            );
          }
        }
      }
    }, 1000); // Check every 1 second
  }

  /**
   * Stop the watchdog timer.
   * @internal
   */
  private stopWatchdog(): void {
    if (this.watchdogTimer) {
      clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }
    this.hasReportedStall = false;
    this.isStalledFallback = false;
    this.stopPlaybackStats();
  }

  /**
   * Start the playback stats reporting timer.
   * @internal
   */
  private startPlaybackStats(): void {
    if (this.playbackStatsTimer) {
      return; // Already running
    }
    this.resetPlaybackStats();
    this.playbackStatsTimer = setInterval(() => {
      this.reportPlaybackStats();
    }, 1000);
  }

  /**
   * Stop the playback stats reporting timer.
   * @internal
   */
  private stopPlaybackStats(): void {
    if (this.playbackStatsTimer) {
      clearInterval(this.playbackStatsTimer);
      this.playbackStatsTimer = null;
    }
    this.resetPlaybackStats();
  }

  /**
   * Reset playback stats counters.
   * @internal
   */
  private resetPlaybackStats(): void {
    this.playbackFrameCount = 0;
    this.playbackFrameTimestamps = [];
    this.playbackSkipCount = 0;
    this.playbackExpectedSeq = -1;
  }

  /**
   * Report playback stats (called every 1s by timer).
   * Logs FPS, the rate of skipped sequences, and playback jitter.
   * @internal
   */
  private reportPlaybackStats(): void {
    // Transition frames are generated locally and are not part of the streamed
    // rate, so no sample is taken while one is playing. The streamed frames
    // rendered before it began still have to be banked first: discarding them
    // here dropped every frame between the last sample and the transition —
    // a measured round rendered 49 frames and reported 41, because the end
    // transition runs for 800ms and the sampler ticks inside it.
    if (this.isPlayingTransition) {
      this.resetPlaybackStats();
      return;
    }

    // Skip if no frames were rendered this interval
    if (this.playbackFrameCount === 0) {
      return;
    }

    // Frames per second over the window's *actual* span, not per tick. The
    // sampler fires every second, but the last window of a round is cut short
    // by the end transition, and a partial window counted as a whole second
    // reports a rate the viewer never saw — a 3s round measured 20fps against a
    // steady 25. Short rounds are hurt worst, which is most of them on RTC.
    const elapsedMs =
      this.playbackFrameTimestamps.length >= 2
        ? this.playbackFrameTimestamps[this.playbackFrameTimestamps.length - 1] -
          this.playbackFrameTimestamps[0]
        : 0;
    // One frame spans no time, so a single-frame window has no rate to report;
    // fall back to the count rather than dividing by zero.
    const fps =
      elapsedMs > 0
        ? Math.round(((this.playbackFrameCount - 1) / elapsedMs) * 1000)
        : this.playbackFrameCount;
    const totalExpected = this.playbackFrameCount + this.playbackSkipCount;
    const skipRate =
      totalExpected > 0 ? (this.playbackSkipCount / totalExpected) * 100 : 0;

    // Calculate jitter: std deviation of inter-frame intervals
    let jitter = 0;
    if (this.playbackFrameTimestamps.length >= 2) {
      const intervals: number[] = [];
      for (let i = 1; i < this.playbackFrameTimestamps.length; i++) {
        intervals.push(
          this.playbackFrameTimestamps[i] - this.playbackFrameTimestamps[i - 1],
        );
      }
      const mean = intervals.reduce((a, b) => a + b, 0) / intervals.length;
      const variance =
        intervals.reduce((sum, v) => sum + (v - mean) ** 2, 0) /
        intervals.length;
      jitter = Math.sqrt(variance);
    }

    logger.info(
      'AnimationHandler',
      `Playback stats: fps=${fps}, skipRate=${skipRate.toFixed(1)}%, jitter=${jitter.toFixed(1)}ms`,
    );

    // Reset counters for next interval
    this.playbackFrameCount = 0;
    this.playbackFrameTimestamps = [];
    this.playbackSkipCount = 0;
  }

  // ── Jitter Buffer ──

  /**
   * Insert a frame into the jitter buffer.
   * Handles dedup, enforces max size, and drives the buffer state machine.
   * @internal
   */
  private bufferFrame(protobufData: ArrayBuffer, seq: number, isRecovered: boolean): void {
    // Never allow frames older than what we've already rendered.
    if (this.lastRenderedFrameSeq >= 0 && seq <= this.lastRenderedFrameSeq) {
      this.conversationJitterStats.jitterDropStale++;
      this.conversationDropped++;
      return;
    }

    // If we're already waiting for a newer sequence, this frame arrived too late.
    if (this.bufferNextSeq >= 0 && seq < this.bufferNextSeq) {
      this.conversationJitterStats.jitterDropLate++;
      this.conversationDropped++;
      return;
    }

    // Dedup — server sends each frame twice for resilience
    if (this.frameBuffer.has(seq)) {
      return;
    }

    this.frameBuffer.set(seq, { protobufData, seq, receivedAt: performance.now(), isRecovered });

    // Enforce max buffer size — drop oldest when full
    if (this.frameBuffer.size > AnimationHandler.BUFFER_MAX_SIZE) {
      let oldestSeq = Infinity;
      for (const k of this.frameBuffer.keys()) {
        if (k < oldestSeq) oldestSeq = k;
      }
      this.frameBuffer.delete(oldestSeq);
      this.conversationJitterStats.jitterDropOverflow++;
      this.conversationDropped++;
    }

    // State machine transitions
    switch (this.bufferState) {
      case 'direct':
        this.setBufferState('filling');
        if (this.bufferNextSeq < 0) {
          this.bufferNextSeq = seq;
        }
        logger.info(
          'AnimationHandler',
          `Jitter buffer: filling (first frame seq=${seq})`,
        );
        if (this.hasFilledEnoughToStart(seq)) {
          this.startBufferDrain();
        }
        break;
      case 'filling':
        if (this.hasFilledEnoughToStart(seq)) {
          this.startBufferDrain();
        }
        break;
      case 'starved':
        this.startBufferDrain();
        break;
      case 'draining':
        // A frame the drain loop is *already waiting on* — it spun a cycle
        // without rendering, so the picture is frozen and this frame is late
        // (an ALR packet recovering a loss ~20ms on, say). Rendering it right
        // away shortens a freeze that is already happening.
        //
        // An open stall run is what says the loop is waiting. Without that
        // check — matching only on the sequence and an armed timer — every
        // frame qualified, because bufferNextSeq advances past each render and
        // the timer is always armed while draining. Each frame was then drawn
        // the moment it arrived instead of at its slot, so the buffer never
        // held anything back and playback simply replayed the network's
        // arrival pattern: a stream landing 30ms/48ms apart was drawn
        // 30ms/48ms apart. Absorbing exactly that is what the buffer is for.
        //
        // Never while a transition is playing: the clock's slots belong to it,
        // and rendering here would put a second writer on the renderer.
        if (
          seq === this.bufferNextSeq &&
          this.playbackTimer !== null &&
          !this.isPlayingTransition &&
          this.conversationQuality.openStall !== null
        ) {
          this.stopPlaybackClock();
          // The frame fills the slot it was already due at, so the grid
          // advances exactly as it would have on the tick this replaces.
          this.scheduleNextSlot(this.drainBufferFrame());
        }
        break;
    }
  }

  /**
   * Whether enough of the round has arrived to start draining.
   *
   * The measure is how far the *server* has got, not how many frames happen to
   * be in hand: sequence numbers start at 0 and advance one per frame interval,
   * so seq N having been sent means N intervals of this round have already
   * elapsed — and the audio, which the driving service paces to match, is that
   * far in too.
   *
   * Counting frames instead (`size >= BUFFER_INITIAL_FILL`) gives the same
   * answer whenever nothing is lost, but not otherwise. If seq 0 and 1 are both
   * dropped and seq 2 is the first to land, the buffer holds one frame and the
   * old check waited for seq 3 — spending another interval filling up while the
   * audio was already two intervals ahead. The picture then trails the sound for
   * the rest of the round, since the drain grid never catches up. Filling a
   * buffer is worth an interval only when there is time to spare, which is
   * exactly what a lossy start does not have.
   * @internal
   */
  private hasFilledEnoughToStart(latestSeq: number): boolean {
    return latestSeq >= AnimationHandler.BUFFER_INITIAL_FILL - 1;
  }

  /**
   * Drop buffered frames that are now too old to ever be rendered in-order.
   * @internal
   */
  private dropStaleBufferedFrames(): void {
    if (this.frameBuffer.size === 0) {
      return;
    }

    const minAllowedSeq = Math.max(
      this.bufferNextSeq,
      this.lastRenderedFrameSeq + 1,
    );
    if (minAllowedSeq < 0) {
      return;
    }

    let dropped = 0;
    for (const seq of Array.from(this.frameBuffer.keys())) {
      if (seq < minAllowedSeq) {
        this.frameBuffer.delete(seq);
        dropped++;
      }
    }

    if (dropped > 0) {
      this.conversationJitterStats.jitterPruneStale += dropped;
      this.conversationDropped += dropped;
    }
  }

  /**
   * Find the lowest buffered sequence at or after minSeq.
   * @internal
   */
  private findLowestBufferedSeqAtOrAfter(minSeq: number): number | null {
    let candidate = Infinity;
    for (const seq of this.frameBuffer.keys()) {
      if (seq >= minSeq && seq < candidate) {
        candidate = seq;
      }
    }
    return candidate === Infinity ? null : candidate;
  }

  /**
   * Begin draining the buffer at 25fps.
   *
   * When a transition is already playing, the clock is running and its slots
   * are spoken for; the buffer simply becomes the source the tick falls
   * through to once the transition queue empties. Starting a second clock here
   * is what used to put two writers on the renderer at once.
   * @internal
   */
  private startBufferDrain(): void {
    this.setBufferState('draining');
    if (this.bufferNextSeq < 0) {
      let minSeq = Infinity;
      for (const k of this.frameBuffer.keys()) {
        if (k < minSeq) minSeq = k;
      }
      this.bufferNextSeq = minSeq;
    }
    logger.info(
      'AnimationHandler',
      `Jitter buffer: draining (${this.frameBuffer.size} frames buffered)`,
    );
    this.startPlaybackClock();
  }

  /**
   * Start the playback clock if it is not already running.
   *
   * `resumeGrid` keeps the existing grid instead of taking the current instant
   * as the first slot. The end transition resumes onto slots the stream was
   * already filling, so re-anchoring there would place its opening frame
   * wherever generation happened to finish — measured at 7ms after the last
   * stream frame, against a 40ms interval. Playback that is genuinely starting
   * over (the opening transition, or a recovery from starvation) has no grid
   * worth keeping and re-anchors.
   * @internal
   */
  private startPlaybackClock(resumeGrid = false): void {
    if (this.playbackTimer !== null) {
      return;
    }
    if (resumeGrid) {
      const now = performance.now();
      const interval = AnimationHandler.FRAME_INTERVAL_MS;
      // Generation ate part of a slot — sometimes more than one — so the grid
      // may already point into the past. Step to the first slot still ahead of
      // us and wait for it.
      //
      // Aiming at a slot that has passed is what a plain `max(0, target - now)`
      // did: the delay came out zero and the transition opened 13ms after the
      // last stream frame, the very thing resuming the grid is meant to avoid.
      // Re-anchoring to `now` produced the same tiny gap by another route.
      //
      // Only worth resuming while the grid is still current. More than a slot
      // behind means nothing was pacing playback — the buffer was disabled or
      // the round never started one, leaving the grid at its initial zero — so
      // there is no cadence to line up with and the transition simply starts
      // now. Stepping to that stale grid would delay the first frame by up to
      // a full interval for no reason.
      const behind = now - this.playbackGridSlot;
      if (behind < interval) {
        const target =
          behind >= 0
            ? this.playbackGridSlot + interval
            : this.playbackGridSlot;
        this.playbackGridSlot = target;
        this.playbackTimer = setTimeout(() => this.playbackTick(), target - now);
        return;
      }
      // Fall through and re-anchor.
    }
    this.playbackGridSlot = performance.now();
    this.playbackTick();
  }

  /**
   * Stop the playback clock.
   * @internal
   */
  private stopPlaybackClock(): void {
    if (this.playbackTimer !== null) {
      clearTimeout(this.playbackTimer);
      this.playbackTimer = null;
    }
  }

  /**
   * Fill one slot on the playback grid, then schedule the next.
   *
   * Transition frames take precedence over buffered stream frames: a start
   * transition is the run-up into the stream and an end transition replaces
   * whatever is left of it, so whenever the transition queue holds a frame it
   * is the one due. Only when the queue is empty does the slot fall through to
   * the jitter buffer. One decision per slot, made in one place, is what makes
   * the hand-over land exactly one interval after the transition's last frame
   * instead of the 28ms two independent timers produced.
   * @internal
   */
  private playbackTick(): void {
    this.playbackTimer = null;

    const rendered = this.isPlayingTransition
      ? this.playTransitionFrame()
      : this.drainBufferFrame();

    // A booked end transition waits out the slots still owed to the queued
    // frames (the jitter buffer's initial fill) before taking over. Only a slot
    // that actually put a frame on screen counts against the debt: an empty one
    // repaid nothing, and letting it count would hand over early — exactly the
    // cut-short ending the debt exists to prevent.
    if (this.pendingEndTransitionFrames !== null) {
      if (rendered) {
        this.endTransitionSlotsOwed--;
      }
      // A slot that rendered nothing while still draining is waiting on a frame
      // that has not arrived yet — it is coming, so keep waiting. Only a
      // starved buffer means nothing more will come: the round has ended, so
      // there is nothing left to refill it and the debt cannot be repaid.
      const nothingLeftToPlay = this.bufferState !== 'draining';
      if (this.endTransitionSlotsOwed <= 0 || nothingLeftToPlay) {
        void this.beginEndTransition();
        return;
      }
    }

    this.scheduleNextSlot(rendered);
  }

  /**
   * Arm the clock for the slot after the one just handled.
   *
   * `rendered` is the only thing the grid advances on. A slot that rendered
   * nothing was never filled, so the frame that eventually fills it is still
   * due there rather than one interval later.
   *
   * Advancing on an empty slot instead pushed every subsequent frame a full
   * interval late; leaving the grid untouched on a filled one left the timer
   * armed for a slot already used, firing within a few ms to find the buffer
   * empty. At a buffer depth of one, where nearly every frame arrives to an
   * empty buffer, that produced a spurious empty tick after almost every
   * frame — renders alternating ~29ms/~48ms apart, each empty tick recorded as
   * a stall spanning nearly the whole gap.
   * @internal
   */
  private scheduleNextSlot(rendered: boolean): void {
    if (!this.isPlayingTransition && this.bufferState !== 'draining') {
      // Nothing left to pace: no transition is running and the buffer is not
      // draining (starved, flushed, or back to idle). Whichever source resumes
      // restarts the clock.
      return;
    }

    const now = performance.now();
    const interval = AnimationHandler.FRAME_INTERVAL_MS;

    let delay: number;
    if (rendered) {
      // Resynchronise when the grid has fallen behind — a backgrounded tab or
      // a blocked main thread — so the backlog does not fire back to back.
      // Catching up would flush the remaining frames in an instant, which
      // looks exactly like the jump a transition exists to smooth over.
      const base = Math.max(this.playbackGridSlot, now - interval);
      this.playbackGridSlot = base + interval;
      delay = Math.max(0, this.playbackGridSlot - now);
    } else {
      // The slot stays where it is and the frame that fills it is still due
      // there. Only the retry is pushed out: re-running immediately would spin
      // the loop at zero delay.
      delay = interval;
    }

    this.playbackTimer = setTimeout(() => this.playbackTick(), delay);
  }

  /**
   * Take one frame from the buffer for the current slot.
   * Handles missing frames (skip-ahead after maxBufferDelayMs) and starvation.
   * @returns whether a frame was rendered.
   * @internal
   */
  private drainBufferFrame(): boolean {
    if (this.bufferState !== 'draining') {
      return false;
    }

    const renderedBefore = this.renderedFrameCount;

    this.dropStaleBufferedFrames();

    const frame = this.frameBuffer.get(this.bufferNextSeq);

    if (frame) {
      // A frame rendering normally ends any stall run in progress.
      this.closeStallRun();
      // Expected frame found — render it
      this.renderBufferedFrame(frame);
      this.frameBuffer.delete(this.bufferNextSeq);
      this.bufferNextSeq++;
    } else if (this.frameBuffer.size > 0) {
      // Expected frame missing — check if we should skip ahead to the next in-order frame.
      const nextSeq = this.findLowestBufferedSeqAtOrAfter(this.bufferNextSeq);

      if (nextSeq === null) {
        this.conversationJitterStats.jitterNoInOrder++;
        this.enterStarvedState();
        return false;
      }

      const nextFrame = this.frameBuffer.get(nextSeq)!;
      const waitTime = performance.now() - nextFrame.receivedAt;

      if (waitTime > this.config.maxBufferDelayMs) {
        // Missing frame didn't arrive in time — skip ahead to the next available sequence.
        const gap = Math.max(0, nextSeq - this.bufferNextSeq);
        if (gap > 0) {
          this.conversationQuality.skipped.push({
            from: this.bufferNextSeq,
            to: nextSeq - 1,
            at: performance.now(),
          });
        }
        // Giving up on the missing frame also ends the stall it caused.
        this.closeStallRun();
        this.playbackSkipCount += gap;
        this.conversationSkipped += gap;
        this.conversationJitterStats.jitterSkipEvents++;
        this.conversationJitterStats.jitterSkipFrames += gap;
        this.renderBufferedFrame(nextFrame);
        this.frameBuffer.delete(nextSeq);
        this.bufferNextSeq = nextSeq + 1;
      } else {
        // Waiting for the missing frame: this cycle renders nothing, so the
        // picture is frozen on the previous frame for another interval.
        this.noteStallTick();
      }
    } else {
      // Buffer empty — this cycle renders nothing either, so the picture is
      // frozen exactly as it is when waiting on a gap. Recording it here is
      // what the stall counters were missing: the clock stops when the buffer
      // starves, so nothing else would ever mark this freeze, and a throttled
      // connection that visibly froze reported stall_count 0. The freeze is
      // closed when a frame finally renders, and its duration comes from the
      // clock, so the loop stopping does not shorten it.
      this.noteStallTick();
      this.enterStarvedState();
      return false;
    }

    return this.renderedFrameCount > renderedBefore;
  }

  /**
   * Record the round's numeric quality measures as metrics.
   *
   * Labels stay low cardinality — provider and end reason only. Identifiers
   * (conversation_id, sequence numbers) would multiply the stored series and
   * live on the event instead.
   * @internal
   */
  private recordPlaybackMetrics(values: {
    durationMs: number;
    avgFps: number;
    skipRate: number;
    stallTotalMs: number;
    stallMaxMs: number;
    stallRate: number;
  }): void {
    const quality = this.conversationQuality;
    const labels = {
      provider: this.providerName,
      end_reason: quality.endReason,
    };

    recordMetric('rtc_playback_duration_ms', values.durationMs, labels);
    recordMetric('rtc_playback_avg_fps', values.avgFps, labels);
    recordMetric('rtc_playback_frame_count', this.conversationFrameCount, labels);
    recordMetric('rtc_playback_skipped_frames', this.conversationSkipped, labels);
    recordMetric('rtc_playback_skip_rate_pct', values.skipRate, labels);
    recordMetric('rtc_playback_stall_count', quality.stalls.length, labels);
    recordMetric('rtc_playback_stall_total_ms', values.stallTotalMs, labels);
    recordMetric('rtc_playback_stall_max_ms', values.stallMaxMs, labels);
    // The share, not just the totals: a 500ms freeze means something very
    // different in a 2s round than in a 60s one, and only the ratio makes
    // those two comparable in one distribution.
    recordMetric('rtc_playback_stall_rate_pct', values.stallRate, labels);
    recordMetric('rtc_playback_tail_discarded', quality.tailDiscarded, labels);
    // Shortfall rather than the raw counts: zero is the healthy value, so any
    // non-zero reading is a transition the viewer saw cut short.
    recordMetric(
      'rtc_playback_start_transition_missing',
      Math.max(
        0,
        quality.startTransitionExpected - quality.startTransitionRendered,
      ),
      labels,
    );
    recordMetric(
      'rtc_playback_end_transition_missing',
      Math.max(
        0,
        quality.endTransitionExpected - quality.endTransitionRendered,
      ),
      labels,
    );
  }

  /**
   * Emit the round as a trace: a root span covering the round, with a child
   * span per stall and per skipped range so the waterfall shows where playback
   * actually broke down, which the aggregate counts cannot convey.
   * @internal
   */
  private emitPlaybackTrace(durationMs: number, stallTotalMs: number): void {
    if (!this.conversationId || !this.conversationStartTime) return;

    const quality = this.conversationQuality;
    const startMs = this.conversationStartTime;
    const endMs = startMs + durationMs;

    try {
      const playbackTrace = startPlaybackTrace(
        this.conversationId,
        startMs,
        {
          provider: this.providerName,
          end_reason: quality.endReason,
        },
        this.serverTraceparent,
      );

      // Every mark is collected on the monotonic clock and converted here, in
      // one place. Mixing the two clocks at the collection sites made spans
      // drift apart whenever the main thread was blocked, since the wall clock
      // and performance.now() diverge under load.
      //
      // Conversion goes through avatarkit's calibrated clock so these spans land
      // on the same timeline as its own and as the server's records; a client
      // whose wall clock is off would otherwise file the round at a time that
      // matches nothing. Falls back to this round's own anchor when avatarkit
      // has not initialized, which is also what the fallback below covers.
      const monoToWall = (mono: number): number =>
        resolveMonoTimestamp(mono) ??
        startMs + (mono - this.conversationStartMono);

      // The round is laid out as one unbroken chain of spans — transitions,
      // playing stretches and freezes end to end, adding up to the full
      // duration — rather than a few isolated markers floating over an empty
      // track. Read as a waterfall it shows when playback started, where it
      // froze, and when it picked up again.
      const startTransitionEnd = quality.startTransitionEndedAt
        ? monoToWall(quality.startTransitionEndedAt)
        : startMs;
      if (quality.startTransitionBeganAt) {
        playbackTrace.span(
          'start_transition',
          monoToWall(quality.startTransitionBeganAt),
          startTransitionEnd,
          {
            dur_ms: Math.round(
              startTransitionEnd - monoToWall(quality.startTransitionBeganAt),
            ),
            frames_expected: quality.startTransitionExpected,
            frames_rendered: quality.startTransitionRendered,
          },
          // A transition cut short is the visible jump into speech.
          quality.startTransitionRendered < quality.startTransitionExpected,
        );
      }

      // Animation frames run from the end of the start transition until the end
      // transition takes over.
      const speakingEnd = quality.endTransitionBeganAt
        ? monoToWall(quality.endTransitionBeganAt)
        : endMs;
      let cursor = startTransitionEnd;
      let playIdx = 0;
      let stallIdx = 0;
      for (const run of quality.stalls) {
        const runStart = Math.max(monoToWall(run.startedAt), cursor);
        const runEnd = Math.min(monoToWall(run.endedAt), speakingEnd);
        if (runStart > cursor) {
          playbackTrace.span(`playing_${++playIdx}`, cursor, runStart, {
            dur_ms: Math.round(runStart - cursor),
          });
        }
        if (runEnd > runStart) {
          playbackTrace.span(
            `stall_${++stallIdx}`,
            runStart,
            runEnd,
            {
              seq: run.seq,
              dur_ms: Math.round(runEnd - runStart),
              ticks: run.ticks,
              starved: run.starved,
            },
            true,
          );
        }
        cursor = runEnd;
      }
      if (speakingEnd > cursor) {
        playbackTrace.span(`playing_${++playIdx}`, cursor, speakingEnd, {
          dur_ms: Math.round(speakingEnd - cursor),
        });
      }

      if (quality.endTransitionBeganAt) {
        playbackTrace.span(
          'end_transition',
          monoToWall(quality.endTransitionBeganAt),
          endMs,
          {
            dur_ms: Math.round(
              endMs - monoToWall(quality.endTransitionBeganAt),
            ),
            frames_expected: quality.endTransitionExpected,
            frames_rendered: quality.endTransitionRendered,
          },
          quality.endTransitionRendered < quality.endTransitionExpected,
        );
      }

      // Placed at the moment the drain loop gave up on the missing frames, so
      // each one lines up with the stall it ended.
      let skipIdx = 0;
      for (const range of quality.skipped) {
        playbackTrace.span(
          `skip_${++skipIdx}`,
          monoToWall(range.at),
          monoToWall(range.at),
          {
            from_seq: range.from,
            to_seq: range.to,
            frames: range.to - range.from + 1,
          },
          true,
        );
      }

      // Stalls and skips are marked on the child spans; the round itself is not
      // a failure, so the root closes OK.
      playbackTrace.end(true, endMs, {
        frame_count: this.conversationFrameCount,
        skipped_frames: this.conversationSkipped,
        stall_count: quality.stalls.length,
        stall_total_ms: stallTotalMs,
        tail_intact: quality.tailIntact ?? false,
        duration_ms: durationMs,
        });
    } catch {
      // Never let telemetry break playback.
    }
  }

  /**
   * Whether the given payload is byte-identical to the last frame rendered.
   * @internal
   */
  private isSameFrameAsLastRendered(
    protobufData: ArrayBuffer | Uint8Array,
  ): boolean {
    const last = this.lastRenderedPayload;
    if (!last) return false;
    const incoming =
      protobufData instanceof Uint8Array
        ? protobufData
        : new Uint8Array(protobufData);
    if (incoming.byteLength !== last.byteLength) return false;
    for (let i = 0; i < incoming.length; i++) {
      if (incoming[i] !== last[i]) return false;
    }
    return true;
  }

  /**
   * How long one freeze kept the picture still, in ms of real elapsed time.
   * @internal
   */
  private static stallRunDurationMs(run: StallRun): number {
    return Math.max(0, Math.round(run.endedAt - run.startedAt));
  }

  /**
   * Record that this drain cycle put no new frame on screen, opening a freeze
   * if one is not already in progress.
   *
   * Every path that fails to render calls this, whatever the reason — a gap in
   * the sequence, or nothing buffered at all. The viewer sees one thing in both
   * cases: the picture stopped changing. Consecutive cycles extend the freeze
   * in progress rather than starting a new one, so a three-second freeze is one
   * stall, not seventy-five.
   * @internal
   */
  private noteStallTick(): void {
    const quality = this.conversationQuality;
    const now = performance.now();
    if (quality.openStall) {
      quality.openStall.ticks++;
      return;
    }
    quality.openStall = {
      seq: this.bufferNextSeq,
      startedAt: now,
      endedAt: now,
      ticks: 1,
      starved: this.frameBuffer.size === 0,
    };
  }

  /**
   * Close the freeze in progress, if any, and file it.
   *
   * Called at the moment a frame finally reaches the screen (or the round
   * ends), so the closing timestamp is when the picture started moving again —
   * which is what makes the duration real elapsed time rather than a count of
   * scheduler cycles.
   * @internal
   */
  private closeStallRun(): void {
    const quality = this.conversationQuality;
    if (!quality.openStall) return;
    quality.openStall.endedAt = performance.now();
    quality.stalls.push(quality.openStall);
    quality.openStall = null;
  }

  /**
   * Render a single frame from the buffer and collect playback stats.
   * @internal
   */
  private renderBufferedFrame(frame: BufferedFrame): void {
    if (
      this.lastRenderedFrameSeq >= 0 &&
      frame.seq <= this.lastRenderedFrameSeq
    ) {
      this.conversationJitterStats.jitterOutOfOrderRenderRejects++;
      this.conversationDropped++;
      return;
    }

    this.renderer.renderFromProtobuf(frame.protobufData);
    this.lastRenderedPayload = new Uint8Array(frame.protobufData);
    this.lastRenderedFrameSeq = frame.seq;
    this.renderedFrameCount++;
    if (frame.isRecovered) this.conversationRecovered++;
    this.logRenderedFrame('buffer', frame.seq, frame.isRecovered);

    // Playback stats
    this.playbackFrameTimestamps.push(performance.now());
    this.playbackFrameCount++;
    this.conversationFrameCount++;
  }

  /**
   * Flush the jitter buffer and revert to direct mode.
   *
   * The clock keeps running if a transition still needs pacing — the end
   * transition is started immediately after this, and stopping the clock here
   * would leave it with nothing to drive it.
   * @internal
   */
  private flushBuffer(): void {
    this.frameBuffer.clear();
    this.setBufferState('direct');
    this.bufferNextSeq = -1;
    if (!this.isPlayingTransition) {
      this.stopPlaybackClock();
    }
  }

  /**
   * Put the current transition frame on screen for this slot.
   *
   * Pacing belongs to the playback clock; this only renders. When the queue
   * runs out the transition ends here, and the slot is left unfilled so the
   * clock hands the next one to whatever comes after — the jitter buffer for a
   * start transition, nothing at all for an end transition.
   * @returns whether a frame was rendered.
   * @internal
   */
  private playTransitionFrame(): boolean {
    if (
      !this.isPlayingTransition ||
      this.transitionFrameIndex >= this.transitionFrames.length
    ) {
      // Transition complete
      const wasTransitioningToIdle = this.isTransitioningToIdle;
      this.isPlayingTransition = false;
      this.isTransitioningToIdle = false;
      this.transitionFrames = [];
      this.transitionFrameIndex = 0;
      logger.info('AnimationHandler', 'Transition playback complete');

      // If transitioning to idle, start idle animation
      if (wasTransitioningToIdle) {
        logger.info(
          'AnimationHandler',
          'Starting idle animation after transition',
        );
        // This is where a normal round actually finishes: the end transition
        // has played out and the avatar is back to idle. Report here rather
        // than in startIdle(), which this path never calls — only the abnormal
        // endings (stall fallback, bare idle packet) go through that.
        this.closeStallRun();
        this.reportConversationStats();
        // Reset session tracking before going idle. Otherwise the next
        // conversation's start-transition packet — which arrives before the
        // first animation frame (and thus before resetTracking runs) — hits
        // the "playback already started" guard (isInSession + lastRenderedSeq)
        // and gets dropped, leaving the next conversation with no start
        // transition (visible jump on the opening frame).
        this.isInSession = false;
        this.lastRenderedFrameSeq = -1;
        this.hasHandledTransitionStart = false;
        // The end transition lands exactly on idle[0], so resetting AvatarKit's
        // idle frame index here is what makes the idle loop resume seamlessly —
        // render unconditionally, and mark idle so the trailing idle packets
        // that follow are ignored.
        this.isIdle = true;
        this.renderer.renderFrame(undefined, true);
        this.logRenderedFrame('idle');
        return true;
      }

      // A start transition just ran out, and this slot is still unfilled. The
      // stream is what continues the motion, so hand the slot straight to the
      // buffer instead of ending the tick: the transition's last frame and the
      // stream's first are consecutive frames of one sequence and belong one
      // interval apart. Returning here instead would leave this slot empty and
      // open a two-interval hole exactly at the hand-over.
      return this.drainBufferFrame();
    }

    if (!this.renderer.isReady()) {
      this.isPlayingTransition = false;
      this.isTransitioningToIdle = false;
      return false;
    }

    const frame = this.transitionFrames[this.transitionFrameIndex];
    this.isIdle = false;
    this.renderer.renderFrame(frame);
    // The picture moved, so any freeze is over — a transition frame ends it
    // exactly as a stream frame does. Closing on the render itself rather than
    // when the transition was booked keeps the run's end at the moment the
    // viewer saw motion again.
    this.closeStallRun();
    this.logRenderedFrame('transition');
    this.transitionFrameIndex++;
    // Counted per frame rather than from the generated length: a transition can
    // be cut short (the start one is interrupted by the first animation frame),
    // and the gap between expected and rendered is the visible jump.
    const quality = this.conversationQuality;
    const now = performance.now();
    if (this.isTransitioningToIdle) {
      quality.endTransitionRendered++;
      if (!quality.endTransitionBeganAt) quality.endTransitionBeganAt = now;
    } else {
      quality.startTransitionRendered++;
      if (!quality.startTransitionBeganAt) quality.startTransitionBeganAt = now;
      // Moves with each frame, so it ends up at the last one played — the start
      // transition can be cut short, and that final frame is where the
      // animation frames take over.
      //
      // One interval past that render, not the render itself: the frame still
      // occupies its own slot, and the transition is over only when the slot
      // ends and the stream's first frame is due. Nothing can claim that slot
      // early — pre-emption is barred while a transition is playing, and the
      // flag only clears on the following tick — so the interval is always
      // served in full and can be added as the constant it is.
      //
      // Ending at the render instead understated the transition by a frame and,
      // because this same instant opens the speaking span, padded that span by
      // the same 40ms: a round streaming a clean 25fps reported 24.
      quality.startTransitionEndedAt = now + AnimationHandler.FRAME_INTERVAL_MS;
    }

    return true;
  }

  /**
   * Emit a per-frame render log for debugging ordering issues.
   * @internal
   */
  private logRenderedFrame(
    source: 'direct' | 'buffer' | 'transition' | 'idle' | 'transition-fallback',
    seq?: number,
    isRecovered?: boolean,
  ): void {
    logger.info(
      'AnimationHandler',
      `Rendered frame: source=${source}, seq=${seq ?? 'n/a'}${isRecovered ? ' [RECOVERED]' : ''}`,
    );
  }


  /**
   * Transition the jitter buffer state.
   * @internal
   */
  private setBufferState(nextState: BufferState): void {
    this.bufferState = nextState;
  }

  /**
   * Enter starved state and emit a one-shot anomaly metric for the transition.
   * @internal
   */
  private enterStarvedState(): void {
    const previousState = this.bufferState;
    this.setBufferState('starved');
    if (previousState === 'starved') {
      return;
    }

    this.conversationJitterStats.jitterStarved++;
    // This counter tracks how often the buffer hit zero. The freeze itself is
    // recorded by the caller, which calls noteStallTick() before getting here:
    // a cycle that pulled no frame is a stall regardless of why, and it ends
    // when a frame is finally pulled. The two are separate measurements —
    // hitting zero and refilling immediately still shows up here, but lasts
    // barely any time and so contributes almost nothing to stall duration.

    // Emitted at most once per round. With the buffer normally holding a single
    // frame, it drains dry constantly and the state flips straight back to
    // draining on the next arrival, so the transition guard above does not stop
    // one record per frame — which is what this event did in practice. The
    // per-round total lives on rtc_playback_stats as jitter_starved.
    if (!this.hasEmittedStarvedMetric) {
      this.hasEmittedStarvedMetric = true;
      this.trackMetric('rtc_jitter_buffer_starved');
    }
  }

  /**
   * Correlation id of the round in progress, or '' between rounds.
   * @internal
   */
  getConversationId(): string {
    return this.conversationId;
  }

  /**
   * Track a jitter-buffer metric with the shared state payload.
   * @internal
   */
  private trackMetric(
    metric: string,
    properties: Record<string, unknown> = {},
  ): void {
    trackMetric(metric, {
      provider: this.providerName,
      conversation_id: this.conversationId,
      buffered_frames: this.frameBuffer.size,
      next_expected_seq: this.bufferNextSeq,
      last_rendered_seq: this.lastRenderedFrameSeq,
      max_buffer_delay_ms: this.config.maxBufferDelayMs,
      ...properties,
    });
  }

  /**
   * Merge source jitter counters into target.
   * @internal
   */
  private mergeJitterStats(
    target: JitterStatsCounters,
    source: JitterStatsCounters,
  ): void {
    target.jitterDropStale += source.jitterDropStale;
    target.jitterDropLate += source.jitterDropLate;
    target.jitterDropOverflow += source.jitterDropOverflow;
    target.jitterPruneStale += source.jitterPruneStale;
    target.jitterSkipEvents += source.jitterSkipEvents;
    target.jitterSkipFrames += source.jitterSkipFrames;
    target.jitterStarved += source.jitterStarved;
    target.jitterNoInOrder += source.jitterNoInOrder;
    target.jitterOutOfOrderRenderRejects +=
      source.jitterOutOfOrderRenderRejects;
  }
}
